import { fetchWithAuth } from '../helpers';
import { getApiUrl } from '../config/api';

// Helper function to handle response
const handleResponse = async (response: Response) => {
  const data = await response.json();
  if (!response.ok) {
    const error = (data && data.message) || response.statusText;
    return Promise.reject(error);
  }
  return data;
};

// Tuition Detail API
const tuitionDetailService = {
  // Get all tuition details with optional filters
  getAll: async (filters?: { month?: number; year?: number; studentId?: number; classId?: number }) => {
    const queryParams = new URLSearchParams();
    if (filters?.month) queryParams.append('month', filters.month.toString());
    if (filters?.year) queryParams.append('year', filters.year.toString());
    if (filters?.studentId) queryParams.append('studentId', filters.studentId.toString());
    if (filters?.classId) queryParams.append('classId', filters.classId.toString());

    const url = `${getApiUrl()}/api/tuition-details/${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
    const response = await fetchWithAuth(url, {
      method: 'GET',
      headers: { "ngrok-skip-browser-warning": "69420" },
    });
    return handleResponse(response);
  },

  // Get tuition detail by ID
  getById: async (id: number) => {
    const response = await fetchWithAuth(`${getApiUrl()}/api/tuition-details/${id}`, {
      method: 'GET',
      headers: { "ngrok-skip-browser-warning": "69420" },
    });
    return handleResponse(response);
  },

  // Create or update tuition detail (UPSERT)
  createOrUpdate: async (tuitionDetail: any) => {
    const response = await fetchWithAuth(`${getApiUrl()}/api/tuition-details/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        "ngrok-skip-browser-warning": "69420",
      },
      body: JSON.stringify(tuitionDetail),
    });
    return handleResponse(response);
  },

  // Batch create or update tuition details
  batchCreateOrUpdate: async (tuitionDetails: any[]) => {
    const response = await fetchWithAuth(`${getApiUrl()}/api/tuition-details/batch`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        "ngrok-skip-browser-warning": "69420",
      },
      body: JSON.stringify({ tuitionDetails }),
    });
    return handleResponse(response);
  },

  // Delete tuition detail
  delete: async (id: number) => {
    const response = await fetchWithAuth(`${getApiUrl()}/api/tuition-details/${id}`, {
      method: 'DELETE',
      headers: { "ngrok-skip-browser-warning": "69420" },
    });
    return handleResponse(response);
  },

  // Get statistics
  getStatistics: async (filters: { month?: number; year?: number; classId?: number }) => {
    const queryParams = new URLSearchParams();
    if (filters?.month) queryParams.append('month', filters.month.toString());
    if (filters?.year) queryParams.append('year', filters.year.toString());
    if (filters?.classId) queryParams.append('classId', filters.classId.toString());

    const url = `${getApiUrl()}/api/tuition-details/statistics?${queryParams.toString()}`;
    const response = await fetchWithAuth(url, {
      method: 'GET',
      headers: { "ngrok-skip-browser-warning": "69420" },
    });
    return handleResponse(response);
  },

  // Get unpaid months (no invoice yet) for a list of students
  // Returns: { [studentId]: [{ tuitionDetailId, month, year, totalFee }] }
  getUnpaidMonths: async (studentIds: number[]) => {
    const url = `${getApiUrl()}/api/tuition-details/unpaid-months?studentIds=${studentIds.join(',')}`;
    const response = await fetchWithAuth(url, {
      method: 'GET',
      headers: { "ngrok-skip-browser-warning": "69420" },
    });
    return handleResponse(response);
  },
};

export default tuitionDetailService;
